/**
 * Jixxeo Tap Race — 10 second tap duel. Fastest fingers wins.
 * Both players see live tap counters. On timer end, the higher count wins.
 * Local taps counted client-side, sync'd to opponent via `move` messages.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MpShell } from "./_mpShell";
import { useMpClient } from "./_mpClient";

const DURATION = 10;   // seconds

export default function JixxeoTapRace() {
  const loc = useLocation();
  const challenge = new URLSearchParams(loc.search).get("challenge");
  const [stake, setStake] = useState(0);
  const [started, setStarted] = useState(!!challenge);
  const [key, setKey] = useState(0);

  const [phase, setPhase] = useState("ready"); // ready | count | play | done
  const [countdown, setCountdown] = useState(3);
  const [ttl, setTtl] = useState(DURATION);
  const [my, setMy] = useState(0);
  const [opp, setOpp] = useState(0);
  const timerRef = useRef();
  const throttleRef = useRef(0);

  const client = useMpClient({ game: "tap-race", stake, challenge, autoConnect: started });

  useEffect(() => {
    setPhase("ready"); setCountdown(3); setTtl(DURATION); setMy(0); setOpp(0);
  }, [key]);

  // Kick off countdown when paired
  useEffect(() => {
    if (client.status !== "paired" || phase !== "ready") return;
    setPhase("count");
    let c = 3;
    setCountdown(3);
    const ct = setInterval(() => {
      c -= 1;
      if (c <= 0) {
        clearInterval(ct);
        setPhase("play");
      } else setCountdown(c);
    }, 800);
    return () => clearInterval(ct);
  }, [client.status]); // phase is intentionally excluded so the count interval survives ready -> count

  // Countdown play timer
  useEffect(() => {
    if (phase !== "play") return;
    setTtl(DURATION);
    const t = setInterval(() => {
      setTtl((v) => {
        if (v <= 1) { clearInterval(t); setPhase("done"); return 0; }
        return v - 1;
      });
    }, 1000);
    timerRef.current = t;
    return () => clearInterval(t);
  }, [phase]);

  // Handle opponent tap batches
  useEffect(() => {
    if (!client.lastMove) return;
    const taps = client.lastMove?.move?.taps;
    if (!taps) return;
    setOpp((v) => v + Number(taps));
  }, [client.lastMove]);

  // On done, report result
  useEffect(() => {
    if (phase !== "done") return;
    // slight delay to let final tap batches settle
    const t = setTimeout(() => {
      if (my > opp) client.sendResult("win");
      else if (my < opp) client.sendResult("lose");
      else client.sendResult("tie");
    }, 400);
    return () => clearTimeout(t);
  }, [phase]); // eslint-disable-line

  const tap = () => {
    if (phase !== "play") return;
    setMy((v) => v + 1);
    // Throttle relay: send every 4 taps or 250ms to reduce socket noise
    throttleRef.current += 1;
    if (throttleRef.current >= 4) {
      client.sendMove({ taps: throttleRef.current });
      throttleRef.current = 0;
    }
  };

  // Send tail on done
  useEffect(() => {
    if (phase === "done" && throttleRef.current > 0) {
      client.sendMove({ taps: throttleRef.current });
      throttleRef.current = 0;
    }
  }, [phase]); // eslint-disable-line

  return (
    <MpShell title="Jixxeo Tap Race" accent="#FFE300" game="tap-race" gameSlug="jixxeo-tap-race"
      status={started ? client.status : "idle"}
      self={client.self} opponent={client.opponent}
      settlement={client.settlement} stake={stake} setStake={setStake}
      onFindOpponent={() => { setStarted(true); setTimeout(() => client.connect(), 0); }}
      onRematch={() => { client.close(); setKey((k) => k + 1); setStarted(false); setTimeout(() => { setStarted(true); client.connect(); }, 100); }}
      initialChallenge={challenge}>
      <div data-testid="tap-body" className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#00F0FF]">// you</div>
            <div className="font-heading font-black text-4xl text-[#00F0FF]">{my}</div>
          </div>
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-white/60">{phase === "play" ? "time" : phase === "count" ? "starting" : ""}</div>
            <div className="font-heading font-black text-3xl text-[#FFE300]">
              {phase === "count" ? countdown : phase === "play" ? `${ttl}s` : phase === "done" ? "GO!" : "…"}
            </div>
          </div>
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#FF003C]">// opp</div>
            <div className="font-heading font-black text-4xl text-[#FF003C]">{opp}</div>
          </div>
        </div>

        <button data-testid="tap-btn" onClick={tap}
          disabled={phase !== "play"}
          className="w-full aspect-[3/2] rounded-3xl border-2 border-[#FFE300] bg-[#FFE300]/10 hover:bg-[#FFE300]/20 active:scale-95 transition-all font-heading font-black text-4xl uppercase tracking-tighter select-none touch-none disabled:opacity-40">
          {phase === "play" ? "TAP!" : phase === "count" ? `Starts in ${countdown}` : phase === "done" ? "Time up" : "Waiting"}
        </button>

        <div className="mono text-[10px] uppercase tracking-widest text-white/50 text-center">
          Tap as fast as you can · 10 second sprint
        </div>
      </div>
    </MpShell>
  );
}
